// src/components/sections/Resources.styles.js

const styles = {
  section: {
    paddingTop: "5rem",
    paddingBottom: "5rem",
    position: "relative",
    overflow: "hidden",
  },

  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 2rem",
  },

  header: {
    textAlign: "center",
    marginBottom: "4rem",
    maxWidth: "800px",
    margin: "0 auto 4rem",
  },

  heading: (isDarkMode) => ({
    fontSize: "clamp(2rem, 5vw, 3rem)",
    fontWeight: 800,
    marginBottom: "1rem",
    color: isDarkMode ? "#f9fafb" : "#1f2937",
  }),

  headingHighlight: {
    color: "#3b82f6",
  },

  subheading: (isDarkMode) => ({
    fontSize: "clamp(1rem, 2vw, 1.125rem)",
    color: isDarkMode ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)",
    maxWidth: "800px",
    margin: "0 auto",
    lineHeight: 1.6,
  }),

  resourcesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "2rem",
    marginBottom: "4rem",
    maxWidth: "900px",
    margin: "0 auto",
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
    },
  },

  resourceCard: (isDarkMode, isClickable) => ({
    backgroundColor: isDarkMode
      ? "rgba(31, 41, 55, 0.7)"
      : "rgba(255, 255, 255, 0.7)",
    borderRadius: "0.75rem",
    padding: "2rem",
    border: `1px solid ${
      isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"
    }`,
    boxShadow: isDarkMode
      ? "0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)"
      : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    transition:
      "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    cursor: isClickable ? "pointer" : "default",
    // For SDK & CLI card (non-clickable), add subtle styling without looking disabled
    ...(!isClickable
      ? {
          backgroundImage: isDarkMode
            ? "linear-gradient(to right bottom, rgba(31, 41, 55, 0.7), rgba(38, 50, 67, 0.7))"
            : "linear-gradient(to right bottom, rgba(255, 255, 255, 0.7), rgba(249, 250, 251, 0.9))",
          borderColor: isDarkMode
            ? "rgba(59, 130, 246, 0.15)"
            : "rgba(59, 130, 246, 0.1)",
          boxShadow: isDarkMode
            ? "0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
            : "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
        }
      : {}),
  }),

  cardHover: (isDarkMode) => ({
    transform: "translateY(-5px)",
    boxShadow: isDarkMode
      ? "0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.15)"
      : "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  }),

  // For the non-clickable card hover effect
  nonClickableCardHover: (isDarkMode) => ({
    borderColor: isDarkMode
      ? "rgba(59, 130, 246, 0.3)"
      : "rgba(59, 130, 246, 0.2)",
    boxShadow: isDarkMode
      ? "0 6px 10px -2px rgba(0, 0, 0, 0.25), 0 3px 5px -2px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
      : "0 6px 10px -2px rgba(0, 0, 0, 0.05), 0 3px 5px -2px rgba(0, 0, 0, 0.03), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
  }),

  iconContainer: (isDarkMode, isClickable) => ({
    backgroundColor: isDarkMode
      ? isClickable
        ? "rgba(59, 130, 246, 0.2)"
        : "rgba(59, 130, 246, 0.15)"
      : isClickable
      ? "rgba(59, 130, 246, 0.1)"
      : "rgba(59, 130, 246, 0.07)",
    borderRadius: "0.5rem",
    width: "3rem",
    height: "3rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "1rem",
    color: isClickable ? "#3b82f6" : "#60a5fa",
    transition: "background-color 0.3s ease, transform 0.3s ease",
    boxShadow: isClickable
      ? "none"
      : isDarkMode
      ? "inset 0 1px 2px rgba(0, 0, 0, 0.1)"
      : "inset 0 1px 2px rgba(0, 0, 0, 0.03)",
  }),

  resourceTitle: (isDarkMode, isClickable) => ({
    fontSize: "1.25rem",
    fontWeight: 700,
    marginBottom: "0.75rem",
    color: isDarkMode ? "#f9fafb" : "#1f2937",
    transition: "color 0.3s ease",
  }),

  resourceDescription: (isDarkMode, isClickable) => ({
    fontSize: "0.875rem",
    color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
    lineHeight: 1.6,
    marginBottom: "1.5rem",
    flex: 1,
  }),

  resourceLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem",
    color: "#3b82f6",
    fontWeight: 500,
    fontSize: "0.875rem",
    textDecoration: "none",
    marginTop: "auto",
    transition: "color 0.3s ease",
    position: "relative",
    paddingBottom: "2px",
  },

  // Coming Soon link styled to look elegant but non-clickable
  comingSoonLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem",
    color: "#60a5fa",
    fontWeight: 500,
    fontSize: "0.875rem",
    textDecoration: "none",
    marginTop: "auto",
    cursor: "default",
    pointerEvents: "none",
    position: "relative",
    paddingBottom: "2px",
    opacity: 0.9,
    // Add subtle badge-like effect
    "&::before": {
      content: '"SOON"',
      position: "absolute",
      top: "-1.25rem",
      right: "-1.25rem",
      backgroundColor: "#3b82f6",
      color: "white",
      fontSize: "0.625rem",
      fontWeight: "700",
      padding: "0.125rem 0.375rem",
      borderRadius: "1rem",
      transform: "scale(0.85)",
      letterSpacing: "0.05em",
    },
  },
};

export default styles;
